import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { fadeOutAnimation } from '@commons/sticky-nav/animations';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { CejV2BannerModule } from '@domains/cej-v2/cej-v2-banner/cej-v2-banner.component';
import { CompaniesRoutingEnum } from '@features/companies/companies-routing.enum';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RouterState } from '@ngxs/router-plugin';
import { Store } from '@ngxs/store';
import { ScrollToModule } from '@nicky-lenaers/ngx-scroll-to';
import { WINDOW } from '@wizbii/angular-utilities';
import { trackEventWithAtInternet } from '@wizbii/tracking';
import {
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  slideInDownOnEnterAnimation,
  slideOutUpOnLeaveAnimation,
} from 'angular-animations';
import { BehaviorSubject, combineLatest, fromEvent, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-sticky-nav-top',
  templateUrl: './sticky-nav-top.component.html',
  styleUrls: ['./sticky-nav-top.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    fadeOutAnimation(),
    fadeInOnEnterAnimation({ duration: 400 }),
    fadeOutOnLeaveAnimation({ duration: 300 }),
    slideInDownOnEnterAnimation({ duration: 400, translate: '3.125rem' }),
    slideOutUpOnLeaveAnimation({ duration: 300, translate: '3.125rem' }),
  ],
})
export class StickyNavTopComponent implements OnInit, OnDestroy {
  @Input() displayLocalMissionSecureSpace = false;
  @Input() displayCompanySecureSpace = false;
  @Input() menuOpen = false;
  @Input() triggerAnimOut = false;
  @Input() companyId?: string;
  @Input() eventId?: string;

  @Output() logoutFromSecuredSpace = new EventEmitter<void>();
  @Output() openMobileMenu = new EventEmitter<void>();
  @Output() closeMobileMenu = new EventEmitter<void>();

  FeaturesRoutingEnum = FeaturesRoutingEnum;
  CompaniesRoutingEnum = CompaniesRoutingEnum;
  trackEvent = trackEventWithAtInternet;

  fixedMobileHeader$ = new BehaviorSubject<boolean>(false);

  get isMobileOrTablet$(): Observable<boolean> {
    return this.breakpointsService.isMobileAndTabletPortrait$;
  }

  get isWeb$(): Observable<boolean> {
    return this.breakpointsService.isWeb$;
  }

  bgColor$!: Observable<any>;

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly store: Store,
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any
  ) {}

  ngOnInit(): void {
    combineLatest([fromEvent(this.window, 'scroll', { passive: true }), this.isWeb$.pipe(distinctUntilChanged())])
      .pipe(
        untilDestroyed(this),
        debounceTime(0),
        map(([_, isWeb]) => !isWeb && this.window.scrollY > 30),
        filter((isFixed) => isFixed !== this.fixedMobileHeader$.value)
      )
      .subscribe({
        next: (isFixed) => {
          this.fixedMobileHeader$.next(isFixed);
        },
      });

    this.bgColor$ = this.store.select(RouterState.state).pipe(
      map((state: any) => state.data),
      map((data) => ({ 'background-color': data.headerBgColor ?? 'white' })),
      distinctUntilChanged()
    );
  }

  focusMainContent(): void {
    const elmToFocus = this.document.querySelector(`#mainContent`);

    if (elmToFocus) {
      elmToFocus.focus();
    }
  }

  ngOnDestroy(): void {
    this.fixedMobileHeader$.complete();
  }
}

@NgModule({
  imports: [CommonModule, RouterModule, MatIconModule, MatButtonModule, ScrollToModule, CejV2BannerModule],
  declarations: [StickyNavTopComponent],
  exports: [StickyNavTopComponent],
})
export class StickyNavTopModule {}
